import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ApiMethod } from '../decorators/api-method.decorator';
import { Account } from '../models/auth/account.model';
import { UserPlatformType } from '../models/enum/user-platform-type.enum';
import { TermsOfUse } from '../models/terms-of-use.model';
import { AuthService } from './auth.service';
import { AddClassificationDocumentType, AddClassificationJob, ManagedDocuments } from './ui/models/managed-documents.model';
import { Item } from '../models/item.model';
import { SiteNote } from '../models/site-notes/site-note.model';

@Injectable({
	providedIn: 'root'
})
export class UserAuthService {

	private readonly baseUrl = `${environment.baseUrl}/userauth`;

	constructor(
		private http: HttpClient,
		private authService: AuthService
	) { }

	@ApiMethod()
	public getPlatformTypes(email: string): Observable<UserPlatformType[]> {
		return this.http.get<UserPlatformType[]>(`${this.baseUrl}/${email}/platform-types`);
	}

	@ApiMethod()
	public getAccounts(platformTypeId: number): Observable<Account[]> {
		return this.http.get<Account[]>(`${this.baseUrl}/${platformTypeId}/accounts`, { headers: this.authService.getAuthenticationHeader() });
	}

	@ApiMethod()
	public getTermsOfUse(token: string): Observable<TermsOfUse> {
		return this.http.get<TermsOfUse>(`${this.baseUrl}/${token}/terms-of-use`);
	}

	@ApiMethod()
	public requiresTermsAcceptance(token: string): Observable<boolean> {
		return this.http.get<boolean>(`${this.baseUrl}/${token}/requires-terms-acceptance`);
	}

	@ApiMethod()
	public updateUserTermsAcceptance(token: string): Observable<void> {
		return this.http.post<void>(`${this.baseUrl}/${token}/terms-of-use`, {});
	}

	@ApiMethod()
	public getManagedJobDocuments(documentId: number, documentOid: string): Observable<ManagedDocuments> {
		return this.http.get<ManagedDocuments>(`${this.baseUrl}/managed-documents/${documentOid}/${documentId}/by-useraccount`, { headers: this.authService.getAuthenticationHeader() });
	}

	@ApiMethod()
	public getManagedDocumentsUserAccess(documentId: number, documentOid: string): Observable<ManagedDocuments> {
		return this.http.get<ManagedDocuments>(`${this.baseUrl}/managed-documents/${documentOid}/${documentId}`, { headers: this.authService.getSimpleAuthenticationHeader() });
	}

	@ApiMethod()
	public getManageDocumentsSites(documentOid: string, documentId: number): Observable<Item<number, string>[]> {
		return this.http.get<Item<number, string>[]>(`${this.baseUrl}/managed-documents/sites/${documentId}/${documentOid}`, { headers: this.authService.getAuthenticationHeader() });
	}

	@ApiMethod()
	public getAddClassificationJob(accountSiteId: number, accountSiteResourceId: number, documentTypeId: number, serviceCategoryId?: number, lineOfBusinessId?: number): Observable<AddClassificationJob[]> {
		return this.http.get<AddClassificationJob[]>(`${this.baseUrl}/managed-documents/${documentTypeId}/${accountSiteId}/${accountSiteResourceId}/get-job/${serviceCategoryId}/${lineOfBusinessId}`, { headers: this.authService.getAuthenticationHeader() });
	}

	@ApiMethod()
	public getUserDocumentTypes(accountSiteId: number, documentId: number, documentOid: string, accountSiteResourceId?: number, serviceCategoryId?: number, lineOfBusinessId?: number): Observable<AddClassificationDocumentType[]> {
		return this.http.get<AddClassificationDocumentType[]>(`${this.baseUrl}/managed-documents/${accountSiteId}/${accountSiteResourceId}/${documentId}/${documentOid}/${serviceCategoryId}/${lineOfBusinessId}/document-types`, { headers: this.authService.getAuthenticationHeader() });
	}

	@ApiMethod()
	public getSites(token): Observable<any> {
		return this.http.get<Item<number, string>[]>(`${this.baseUrl}/sites/${token}`);
	}
}
